import type {
  DeploymentLog,
  DialoguePackage,
  DialogueUsage,
  EditorSnapshot,
  EventTestResponse,
  Project,
  ProjectVersion,
  RosterSimulation,
  ServerConnection,
  SkinAsset,
  StudioDocument,
  TemplateCatalog,
  User,
  ValidationResult,
} from './types'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail: unknown,
  ) {
    super(message)
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown }

export class StudioApi {
  private csrfToken: string | null = null

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = (options.method ?? 'GET').toUpperCase()
    const headers = new Headers(options.headers)
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      await this.ensureCsrf()
      headers.set('X-CSRF-Token', this.csrfToken ?? '')
    }
    let body: BodyInit | undefined
    if (options.body instanceof FormData || options.body instanceof Blob) {
      body = options.body
    } else if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(options.body)
    }
    const response = await fetch(path, { ...options, method, headers, body, credentials: 'include' })
    if (response.status === 204) return undefined as T
    const contentType = response.headers.get('content-type') ?? ''
    const payload = contentType.includes('application/json') ? await response.json() : await response.text()
    if (!response.ok) {
      const detail = typeof payload === 'object' && payload !== null && 'detail' in payload ? payload.detail : payload
      const message = typeof detail === 'string' ? detail : `Request failed with status ${response.status}`
      throw new ApiError(message, response.status, detail)
    }
    return payload as T
  }

  async ensureCsrf(): Promise<string> {
    if (this.csrfToken) return this.csrfToken
    const response = await fetch('/api/auth/csrf', { credentials: 'include' })
    if (!response.ok) throw new ApiError('Could not establish a secure browser session', response.status, null)
    const body = (await response.json()) as { csrfToken: string }
    this.csrfToken = body.csrfToken
    return body.csrfToken
  }

  async login(email: string, password: string): Promise<{ user: User; csrfToken: string }> {
    await this.ensureCsrf()
    const result = await this.request<{ user: User; csrfToken: string }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    })
    this.csrfToken = result.csrfToken
    return result
  }

  async consumeMinecraftHandoff(ticket: string): Promise<{ user: User; csrfToken: string; projectId: string | null; connectionId: string | null }> {
    const result = await this.request<{ user: User; csrfToken: string; projectId: string | null; connectionId: string | null }>(
      '/api/auth/minecraft-handoff',
      { method: 'POST', body: { ticket } },
    )
    this.csrfToken = result.csrfToken
    return result
  }

  async logout(): Promise<void> {
    await this.request('/api/auth/logout', { method: 'POST' })
    this.csrfToken = null
  }

  me = () => this.request<User>('/api/auth/me')
  templates = () => this.request<TemplateCatalog>('/api/templates')
  projects = () => this.request<Project[]>('/api/projects')
  createProject = (name: string) => this.request<Project>('/api/projects', { method: 'POST', body: { name } })
  project = (id: string) => this.request<Project>(`/api/projects/${encodeURIComponent(id)}`)
  updateProject = (id: string, changes: { name?: string; state?: 'draft' | 'archived' }) => this.request<Project>(`/api/projects/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes })

  documents<T>(projectId: string, kind: string, filters: { search?: string; tags?: string[]; sort?: string; direction?: string } = {}) {
    const query = new URLSearchParams()
    if (filters.search) query.set('search', filters.search)
    filters.tags?.forEach((tag) => query.append('tag', tag))
    if (filters.sort) query.set('sort', filters.sort)
    if (filters.direction) query.set('direction', filters.direction)
    return this.request<Array<StudioDocument<T>>>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}?${query}`,
    )
  }

  document<T>(projectId: string, kind: string, id: string) {
    return this.request<StudioDocument<T>>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
    )
  }

  saveDocument<T>(projectId: string, kind: string, value: { id: string; name: string; tags: string[]; body: T; baseRevision?: number; reason?: string }) {
    return this.request<StudioDocument<T>>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/${encodeURIComponent(value.id)}`,
      { method: 'PUT', body: { ...value, reason: value.reason ?? 'Explicit save' } },
    )
  }

  deleteDocument(projectId: string, kind: string, id: string) {
    return this.request<{ deleted: boolean }>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    )
  }

  duplicateDocument<T>(projectId: string, kind: string, sourceId: string, id: string, name: string) {
    return this.request<StudioDocument<T>>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/${encodeURIComponent(sourceId)}/duplicate`,
      { method: 'POST', body: { id, name } },
    )
  }

  history<T>(projectId: string, kind: string, id: string) {
    return this.request<Array<{ revision: number; name: string; tags: string[]; body: T; reason: string; actorEmail: string; createdAt: string }>>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/history`,
    )
  }

  rollbackDocument<T>(projectId: string, kind: string, id: string, revision: number) {
    return this.request<StudioDocument<T>>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/rollback/${revision}`,
      { method: 'POST' },
    )
  }

  bulk(projectId: string, kind: string, payload: { ids: string[]; addTags?: string[]; removeTags?: string[]; setFields?: Record<string, unknown> }) {
    return this.request<StudioDocument[]>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/bulk`,
      { method: 'POST', body: payload },
    )
  }

  dependencies(projectId: string, kind: string, id: string) {
    type Reference = { sourceKind: string; sourceId: string; path: string; targetKind: string; targetId: string; name: string }
    return this.request<{ target: { kind: string; id: string; name: string }; outgoing: Reference[]; incoming: Reference[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/dependencies`,
    )
  }

  skins = (projectId: string) => this.request<SkinAsset[]>(`/api/projects/${encodeURIComponent(projectId)}/skins`)
  skinVersions = (projectId: string, assetId: string) => this.request<Array<Record<string, unknown>>>(`/api/projects/${encodeURIComponent(projectId)}/skins/${encodeURIComponent(assetId)}/versions`)
  skinVersion = (versionId: string) => this.request<SkinAsset>(`/api/skins/versions/${encodeURIComponent(versionId)}`)

  uploadSkin(projectId: string, file: File, fields: { name: string; model: string; tags: string[]; assetId?: string }) {
    const data = new FormData()
    data.set('file', file)
    data.set('name', fields.name)
    data.set('model', fields.model)
    data.set('tags', JSON.stringify(fields.tags))
    if (fields.assetId) data.set('asset_id', fields.assetId)
    return this.request<{ assetId: string; versionId: string; sha256: string; deduplicated: boolean; duplicateOf?: unknown; signingStatus: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/skins`,
      { method: 'POST', body: data },
    )
  }

  signSkin = (versionId: string) => this.request<Record<string, unknown>>(`/api/skins/versions/${encodeURIComponent(versionId)}/sign`, { method: 'POST' })
  refreshSkin = (versionId: string) => this.request<Record<string, unknown>>(`/api/skins/versions/${encodeURIComponent(versionId)}/sign/refresh`, { method: 'POST' })
  skinUrl = (versionId: string) => `/api/skins/versions/${encodeURIComponent(versionId)}/original`

  dialogues = (projectId: string) => this.request<Array<Omit<DialoguePackage, 'body' | 'audioBlobs'>>>(`/api/projects/${encodeURIComponent(projectId)}/dialogues`)
  dialogue = (projectId: string, id: string, version?: number) => this.request<DialoguePackage>(`/api/projects/${encodeURIComponent(projectId)}/dialogues/${encodeURIComponent(id)}${version ? `?version=${version}` : ''}`)
  dialogueVersions = (projectId: string, id: string) => this.request<Array<Record<string, unknown>>>(`/api/projects/${encodeURIComponent(projectId)}/dialogues/${encodeURIComponent(id)}/versions`)
  dialogueUsages = (projectId: string, id: string) => this.request<DialogueUsage[]>(`/api/projects/${encodeURIComponent(projectId)}/dialogues/${encodeURIComponent(id)}/usages`)
  activateDialogue = (projectId: string, id: string, version: number) => this.request<DialoguePackage>(`/api/projects/${encodeURIComponent(projectId)}/dialogues/${encodeURIComponent(id)}/activate/${version}`, { method: 'POST' })
  mapDialogueSpeakers = (projectId: string, id: string, mappings: Record<string, string>) => this.request<DialoguePackage>(`/api/projects/${encodeURIComponent(projectId)}/dialogues/${encodeURIComponent(id)}/speaker-mappings`, { method: 'PUT', body: { mappings } })
  dialogueAudioUrl = (projectId: string, id: string, audioId: string, version?: number) => `/api/projects/${encodeURIComponent(projectId)}/dialogues/${encodeURIComponent(id)}/audio/${encodeURIComponent(audioId)}${version ? `?version=${version}` : ''}`

  importDialogue(projectId: string, file: File) {
    const data = new FormData()
    data.set('file', file)
    return this.request<DialoguePackage>(`/api/projects/${encodeURIComponent(projectId)}/dialogues`, { method: 'POST', body: data })
  }

  validation = (projectId: string) => this.request<ValidationResult>(`/api/projects/${encodeURIComponent(projectId)}/validation`)
  roster = (projectId: string, payload: Record<string, unknown>) => this.request<RosterSimulation>(`/api/projects/${encodeURIComponent(projectId)}/simulate/roster`, { method: 'POST', body: payload })
  simulateEvent = (projectId: string, payload: Record<string, unknown>) => this.request<Record<string, unknown>>(`/api/projects/${encodeURIComponent(projectId)}/simulate/event`, { method: 'POST', body: payload })

  versions = (projectId: string) => this.request<ProjectVersion[]>(`/api/projects/${encodeURIComponent(projectId)}/versions`)
  buildBundle = (projectId: string, contentVersion: string) => this.request<Record<string, unknown>>(`/api/projects/${encodeURIComponent(projectId)}/bundles`, { method: 'POST', body: { contentVersion } })
  bundleUrl = (projectId: string, versionId: string) => `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}/bundle`
  connections = () => this.request<ServerConnection[]>('/api/connections')
  saveConnection = (value: { name: string; baseUrl: string; token: string }, id?: string) => this.request<ServerConnection>(id ? `/api/connections/${encodeURIComponent(id)}` : '/api/connections', { method: id ? 'PUT' : 'POST', body: value })
  deleteConnection = (id: string) => this.request(`/api/connections/${encodeURIComponent(id)}`, { method: 'DELETE' })
  connectionStatus = (id: string) => this.request<Record<string, unknown>>(`/api/connections/${encodeURIComponent(id)}/status`)
  publish = (projectId: string, versionId: string, connectionId: string) => this.request<Record<string, unknown>>(`/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}/publish/${encodeURIComponent(connectionId)}`, { method: 'POST' })
  rollback = (projectId: string, projectVersionId: string, connectionId: string) => this.request<Record<string, unknown>>(`/api/projects/${encodeURIComponent(projectId)}/rollback`, { method: 'POST', body: { projectVersionId, connectionId } })
  deployments = (projectId: string) => this.request<DeploymentLog[]>(`/api/projects/${encodeURIComponent(projectId)}/deployments`)
  editorSnapshot = (projectId: string) => this.request<EditorSnapshot>(`/api/projects/${encodeURIComponent(projectId)}/editor`)
  saveEditorSnapshot = (projectId: string, body: Record<string, unknown>, baseEditorRevision?: number) => this.request<EditorSnapshot>(`/api/projects/${encodeURIComponent(projectId)}/editor`, { method: 'PUT', body: { body, baseEditorRevision } })
  pullEditor = (projectId: string, connectionId: string) => this.request<EditorSnapshot>(`/api/projects/${encodeURIComponent(projectId)}/editor/pull/${encodeURIComponent(connectionId)}`, { method: 'POST' })
  pushEditor = (projectId: string, connectionId: string) => this.request<Record<string, unknown>>(`/api/projects/${encodeURIComponent(projectId)}/editor/push/${encodeURIComponent(connectionId)}`, { method: 'POST' })
  testEvent = (projectId: string, connectionId: string, payload: Record<string, unknown>) => this.request<EventTestResponse>(`/api/projects/${encodeURIComponent(projectId)}/events/test/${encodeURIComponent(connectionId)}`, { method: 'POST', body: payload })
  eventTestHistory = (projectId: string) => this.request<import('./types').EventTestHistoryEntry[]>(`/api/projects/${encodeURIComponent(projectId)}/events/test-history`)
  resetEventTestHistory = (projectId: string) => this.request<import('./types').EventTestHistoryReset>(`/api/projects/${encodeURIComponent(projectId)}/events/test-history`, { method: 'DELETE' })
  audit = () => this.request<Array<Record<string, unknown>>>('/api/audit?limit=200')
}

export const api = new StudioApi()
